import React, { useState } from 'react';
import { ShoppingBag, X, Minus, Plus, Trash2, CreditCard, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';

const ShoppingCart = ({ onCheckout }) => {
  const {
    items,
    totalItems,
    totalPrice,
    isOpen,
    toggleCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    isCartEmpty
  } = useCart();

  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
    } else {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleCheckout = () => {
    if (onCheckout) {
      onCheckout(); // Deschide modal-ul de checkout
      toggleCart(); // Închide coșul
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300"
        onClick={toggleCart}
      />
      
      {/* Shopping Cart Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 transform transition-transform duration-300 flex flex-col">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-navy-50 to-gold-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-navy-600 to-navy-700 rounded-full flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Coșul Meu</h2>
              <p className="text-sm text-gray-600">
                {totalItems} {totalItems === 1 ? 'produs' : 'produse'}
              </p>
            </div>
          </div>
          
          <button
            onClick={toggleCart}
            className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Cart Content */}
        <div className="flex-1 overflow-y-auto">
          {isCartEmpty ? (
            // Empty Cart State
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-6">
                <ShoppingBag className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Coșul este gol
              </h3>
              <p className="text-gray-600 mb-6 max-w-sm">
                Adăugați produse din colecția noastră premium pentru a începe comanda.
              </p>
              <button
                onClick={toggleCart}
                className="px-6 py-3 bg-gradient-to-r from-navy-600 to-navy-700 text-white rounded-full font-semibold hover:from-navy-700 hover:to-navy-800 transition-all duration-300"
              >
                Continuă cumpărăturile
              </button>
            </div>
          ) : (
            // Cart Items
            <div className="p-4 sm:p-6 space-y-4">
              {items.map((item) => (
                <div key={item.id} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-shadow duration-300">
                  <div className="flex items-start space-x-4">
                    {/* Product Image */}
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                      <img
                        src={item.images?.[0] || '/placeholder.jpg'}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-2 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600 mb-2">
                        {item.category}
                      </p>
                      
                      {/* Price and Quantity Controls */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-1">
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                            className="w-7 h-7 rounded-md bg-white shadow-sm flex items-center justify-center hover:bg-gray-50 transition-colors duration-200"
                          >
                            <Minus className="w-3 h-3 text-gray-600" />
                          </button>
                          
                          <span className="text-sm font-medium text-gray-900 min-w-[2rem] text-center">
                            {item.quantity}
                          </span>
                          
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            className="w-7 h-7 rounded-md bg-white shadow-sm flex items-center justify-center hover:bg-gray-50 transition-colors duration-200"
                          >
                            <Plus className="w-3 h-3 text-gray-600" />
                          </button>
                        </div>

                        <div className="text-right">
                          <div className="text-lg font-bold text-gray-900">
                            {(parseFloat(item.price) * item.quantity).toFixed(2)} lei
                          </div>
                          {item.quantity > 1 && (
                            <div className="text-xs text-gray-500">
                              {item.price} lei / buc
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="w-8 h-8 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors duration-200 flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              ))}

              {/* Clear Cart Button */}
              {!isCartEmpty && (
                <button
                  onClick={clearCart}
                  className="w-full py-2 text-sm text-red-600 hover:text-red-700 font-medium transition-colors duration-200"
                >
                  Golește coșul
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer with Total and Checkout */}
        {!isCartEmpty && (
          <div className="border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white p-4 sm:p-6">
            {/* Total */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-lg font-semibold text-gray-900">Total:</span>
              <span className="text-2xl font-bold text-navy-600">
                {totalPrice.toFixed(2)} lei
              </span>
            </div>

            {/* Delivery Info */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-emerald-800">
                <span className="font-medium">📦 Transport gratuit</span> pentru comenzi peste 500 lei
              </p>
            </div>

            {/* Checkout Button */}
            <button
              onClick={handleCheckout}
              className="w-full py-4 bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
            >
              <CreditCard className="w-5 h-5" />
              <span>Finalizează comanda</span>
              <ArrowRight className="w-5 h-5" />
            </button>

            {/* Security Badge */}
            <div className="flex items-center justify-center mt-3 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span>Plăți securizate prin Stripe</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ShoppingCart;